/**
 * Client-side Yjs persistence helpers for the sync client.
 *
 * Architecture refs:
 *   §4.5 — Web: y-indexeddb + navigator.storage.persist(). The custom
 *          YjsStorageAdapter wraps IDB in an append-only update log and
 *          is the long-term seam that Capacitor/SQLite will slot into.
 *   Spike 0.C — CRDT determinism. All state enters the Y.Doc exclusively
 *          through `Y.applyUpdate` — never by hand-assembling doc state.
 *
 * Responsibilities:
 *   1. Hydrate a Y.Doc from persisted updates BEFORE the WebSocket provider
 *      connects (so the first render is from local cache, not from the
 *      server).
 *   2. Subscribe to the doc's `update` event and append each update to the
 *      adapter.
 *   3. Enforce an LRU eviction cap (MAX_CACHED_DOCS) so the IDB store does
 *      not grow without bound.
 *   4. `purgeYjsCache` — wipes every stored doc. Called on sign-out so a
 *      new user on the same device does not see the previous user's content.
 *
 * Compaction note: the adapter exposes an append-only update log; there is
 * no compaction helper in the current YjsStorageAdapter interface. When the
 * update count for a doc exceeds COMPACTION_THRESHOLD we replace all
 * individual rows with a single merged snapshot via deleteDoc + saveUpdate
 * of `Y.encodeStateAsUpdate(doc)`. This is safe because Yjs updates are
 * idempotent and commutative; the merged snapshot captures the same CRDT
 * state as applying all individual updates in order.
 *
 * LRU eviction: we keep an in-memory LRU ordered list of docKeys (most-
 * recently-used at the end). When a new docKey would push the count beyond
 * MAX_CACHED_DOCS we evict the least-recently-used doc from IDB. The in-
 * memory list is per-page-load and does not persist — on a fresh page we
 * start with an empty LRU list and the IDB may hold up to MAX_CACHED_DOCS
 * docs from previous sessions. This is acceptable: the cap prevents
 * unbounded growth across sessions while the in-memory list prevents
 * unbounded growth within a single session.
 */

import * as Y from "yjs";
import type { YjsStorageAdapter, YDocId } from "../storage/adapter.js";

// ─── Constants ────────────────────────────────────────────────────────────────

/** Maximum number of docs kept in the IDB cache (LRU policy). */
export const MAX_CACHED_DOCS = 50;

/**
 * When a doc accumulates more than this many individual update rows in IDB,
 * compact them into a single snapshot to keep load times fast.
 */
export const COMPACTION_THRESHOLD = 100;

// ─── LRU tracker (in-memory, per page-load) ──────────────────────────────────

/**
 * In-memory LRU. Key → never (we only care about order). We use a Map so
 * iteration order is insertion order and delete+re-insert moves a key to
 * the "most recently used" end.
 */
const lru: Map<string, true> = new Map();

/**
 * Record a docKey access and return the docKey that should be evicted from IDB
 * (if any). Returns null when the cache is below the cap.
 *
 * Exported for unit testing only.
 */
export function lruAccess(docKey: string): string | null {
  // Move to MRU end (delete + re-insert).
  lru.delete(docKey);
  lru.set(docKey, true);

  if (lru.size > MAX_CACHED_DOCS) {
    // The first entry is the LRU one.
    const lruKey = lru.keys().next().value!;
    lru.delete(lruKey);
    return lruKey;
  }

  return null;
}

/**
 * Reset the in-memory LRU state. Called by `purgeYjsCache` and by tests.
 *
 * Exported for unit testing only.
 */
export function lruReset(): void {
  lru.clear();
}

// ─── Hydration ────────────────────────────────────────────────────────────────

/**
 * Load every persisted update for `docKey` from the adapter and apply them
 * to `doc` via `Y.applyUpdate` in insertion order.
 *
 * This MUST be called before the WebSocket provider's `connect()` so the doc
 * renders from local cache on the first paint, and the WS sync reconciles on
 * top (Yjs merge semantics handle any deltas).
 *
 * Spike 0.C: state enters the doc only through `Y.applyUpdate` — the binary
 * update buffer from IDB is the same format Yjs itself produces, so there is
 * no hand-assembly of doc state.
 *
 * Returns the number of updates applied (0 if none persisted yet).
 */
export async function hydrateDocFromStorage(
  docKey: YDocId,
  doc: Y.Doc,
  adapter: YjsStorageAdapter,
): Promise<number> {
  let updates: Uint8Array[];
  try {
    updates = await adapter.loadDoc(docKey);
  } catch (err) {
    // IDB errors must not crash the editor — fall through to a fresh empty doc
    // and let the WS sync fill it in. Log for diagnostics.
    console.error({ event: "yjs_hydrate_load_error", docKey, err: String(err) });
    return 0;
  }

  if (updates.length === 0) return 0;

  // Apply all updates. Yjs merges are idempotent — applying the same update
  // twice converges to the same state, so any overlap with WS-delivered
  // updates is harmless.
  for (const update of updates) {
    try {
      Y.applyUpdate(doc, update);
    } catch (err) {
      // A corrupted update in IDB must not block the rest of the hydration.
      console.error({ event: "yjs_hydrate_apply_error", docKey, err: String(err) });
    }
  }

  return updates.length;
}

// ─── Persistence subscription ─────────────────────────────────────────────────

/**
 * Subscribe to the doc's `update` event and append each update to the
 * adapter. Also runs LRU eviction if the cache is full, and compacts the
 * update log when it grows past COMPACTION_THRESHOLD.
 *
 * Returns an unsubscribe function — call it in the connection's `destroy()`
 * to stop writing after the session ends.
 */
export function attachUpdatePersistence(
  docKey: YDocId,
  doc: Y.Doc,
  adapter: YjsStorageAdapter,
): () => void {
  // Track how many updates we've appended this session so we know when to
  // compact. We snapshot the IDB row count at attach time lazily.
  let rowCountAtAttach: number | null = null; // null = not yet fetched
  let appendedThisSession = 0;
  let destroyed = false;

  // Fetch the initial row count once (best-effort).
  void adapter
    .loadDoc(docKey)
    .then((rows) => {
      if (!destroyed) rowCountAtAttach = rows.length;
    })
    .catch(() => {
      rowCountAtAttach = 0;
    });

  const onUpdate = (update: Uint8Array, _origin: unknown) => {
    if (destroyed) return;

    // Copy the update so IDB's structured-clone cannot mutate the Yjs
    // internal buffer (same pattern as WebYjsStorageAdapter.saveUpdate).
    const copy = new Uint8Array(update.byteLength);
    copy.set(update);

    void adapter
      .saveUpdate(docKey, copy)
      .then(() => {
        if (destroyed) return;
        appendedThisSession++;

        // Check if we should compact.
        const totalRows = (rowCountAtAttach ?? 0) + appendedThisSession;
        if (totalRows > COMPACTION_THRESHOLD) {
          // Compact: replace all rows with a single snapshot of current state.
          // Update our bookkeeping optimistically — if the compact fails we
          // just let the count keep growing until the next opportunity.
          rowCountAtAttach = 1;
          appendedThisSession = 0;
          void compactDoc(docKey, doc, adapter).catch((err) => {
            console.error({ event: "yjs_compact_error", docKey, err: String(err) });
          });
        }
      })
      .catch((err) => {
        // IDB write errors must not crash the editor. Log, don't throw.
        console.error({ event: "yjs_persist_error", docKey, err: String(err) });
      });
  };

  doc.on("update", onUpdate);

  // Run LRU eviction for this docKey — evict from IDB if the cache is full.
  const evictKey = lruAccess(docKey);
  if (evictKey !== null) {
    void adapter.deleteDoc(evictKey).catch((err) => {
      console.error({ event: "yjs_evict_error", evictKey, err: String(err) });
    });
  }

  return () => {
    destroyed = true;
    doc.off("update", onUpdate);
  };
}

// ─── Compaction ──────────────────────────────────────────────────────────────

/**
 * Replace all stored update rows for `docKey` with a single snapshot of the
 * current doc state via `Y.encodeStateAsUpdate(doc)`.
 *
 * This is safe because:
 *   - `Y.encodeStateAsUpdate(doc)` captures the CRDT state including all
 *     lamport-clock metadata, so a fresh doc built by applying this single
 *     update is byte-identical in observable content to one built from the
 *     original update sequence.
 *   - Spike 0.C: we are still going through Y.applyUpdate at load time;
 *     the compact just produces fewer rows, not a different state.
 *
 * Exported for unit testing.
 */
export async function compactDoc(
  docKey: YDocId,
  doc: Y.Doc,
  adapter: YjsStorageAdapter,
): Promise<void> {
  const snapshot = Y.encodeStateAsUpdate(doc);
  await adapter.deleteDoc(docKey);
  await adapter.saveUpdate(docKey, snapshot);
}

// ─── Purge (sign-out) ─────────────────────────────────────────────────────────

/**
 * COMPLETE wipe of every cached y-doc in the adapter, and reset the in-memory
 * LRU. Called in the sign-out path so a new user on the same device cannot see
 * (or leave on disk) the previous user's content (privacy invariant,
 * architecture.md §4.5).
 *
 * Staleness / isolation contract (the load-bearing fix): this purges via
 * `adapter.clearAll()` — a full store wipe — NOT a walk of the in-memory LRU
 * keys. The earlier LRU-only implementation left docs cached in *previous*
 * page-loads on disk after sign-out (the LRU is rebuilt empty on every page
 * load), so the previous account's draft bytes survived cross-account. The
 * "the next user has no valid docKeys" argument is a soft guarantee; a full
 * wipe is the hard one, and it matches the BodySnapshot fast-paint cache which
 * purges its whole namespace on sign-out (`purgeAllSnapshots` →
 * `storeDelByPrefix`). Both caches must purge identically or the cross-account
 * isolation story has a hole.
 */
export async function purgeYjsCache(adapter: YjsStorageAdapter): Promise<void> {
  // Reset LRU first, before the (async) wipe, so that even if clearAll throws
  // no subsequent access can re-add a now-stale key to the tracker.
  lruReset();

  try {
    await adapter.clearAll();
  } catch (err) {
    // Surface with the underlying cause so callers' try/catch + logging
    // (handleSignOut) still classify the failure (Rule 8 — not swallowed).
    throw new Error("purgeYjsCache: clearAll failed", { cause: err });
  }
}
